import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * OnboardingGuard component enforces onboarding completion.
 * 
 * Flow:
 * 1. If user is not authenticated, allow access (public routes)
 * 2. If user's onboarding is not complete, redirect to /onboarding
 * 3. Otherwise, allow access
 * 
 * Email verification is now enforced at the action level (via verifiedProcedure),
 * not at the route level. Unverified users can browse all read-only routes freely.
 * 
 * This guard should wrap all authenticated routes EXCEPT:
 * - /login
 * - /verify-email
 * - /verify-email/callback
 * - /onboarding
 * - /forgot-password
 * - /reset-password
 * 
 * Requirements: 2.3, 2.6
 */
export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, allow access (public routes)
  if (!isAuthenticated || !user) {
    return <>{children}</>;
  }

  // Check if user needs to complete onboarding
  // Only redirect to onboarding if:
  // 1. User is authenticated
  // 2. Onboarding is not complete
  // 3. Not already on onboarding page
  if (!user.onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // User is authenticated and onboarded - render children
  // Email verification is checked at the action level, not route level
  return <>{children}</>;
};
